/**
 * StripePaymentElement (T35.7.2.2) — the Stripe client render adapter.
 *
 * Mounted by `PaymentHandoff` when `descriptor.primary.processor === 'stripe'`.
 * It loads Stripe.js with the publishable key and mounts the Payment Element
 * against the session's client secret (`descriptor.primary.sessionToken`). Card
 * data is entered into Stripe's iframe — never our DOM (PCI SAQ-A).
 *
 * The real charge is confirmed client-side via Stripe.js; the row finalizes from
 * the Stripe webhook (`.1`'s intake → `resolveSubscriptionStatus`), so this
 * component only renders the secure field + the confirm action and reports
 * success/failure up. It never writes `subscriptions`.
 *
 * Token-only styling (CP5); mobile-first; ≥44px targets.
 */

import { type CSSProperties, useMemo, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Spinner } from '../../components/Spinner';
import type { SessionDescriptor } from './api';

const PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;

/**
 * One Stripe.js promise per publishable key (loaded lazily, reused across mounts).
 * Returns `null` when no key is configured (a keyless tier renders the gated
 * placeholder instead of attempting to load Stripe).
 */
let stripePromise: Promise<Stripe | null> | null = null;
function getStripe(): Promise<Stripe | null> | null {
  if (!PUBLISHABLE_KEY) return null;
  if (!stripePromise) stripePromise = loadStripe(PUBLISHABLE_KEY);
  return stripePromise;
}

const confirmBtnStyle: CSSProperties = {
  marginTop: 'var(--space-3)',
  width: '100%',
  minHeight: '2.75rem',
  border: 0,
  borderRadius: 'var(--radius-lg)',
  background: 'var(--color-accent-primary)',
  color: 'var(--color-text-inverse)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-bold)',
  fontFamily: 'var(--font-family-sans)',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'var(--space-2)',
};

const errorStyle: CSSProperties = {
  marginTop: 'var(--space-2)',
  color: 'var(--color-feedback-error)',
  fontSize: 'var(--font-size-xs)',
};

const keyMissingStyle: CSSProperties = {
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-lg)',
  background: 'var(--color-bg-secondary)',
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--font-size-sm)',
  fontFamily: 'var(--font-family-sans)',
  textAlign: 'center',
};

/** The confirm step — collects the card in Stripe's element and confirms the payment. */
function StripeConfirm({
  onResult,
}: {
  onResult?: ((ok: boolean, reason?: string) => void) | undefined;
}): JSX.Element {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm(): Promise<void> {
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);
    // No return_url redirect — confirm in place; the webhook finalizes the row.
    const result = await stripe.confirmPayment({ elements, redirect: 'if_required' });
    setSubmitting(false);
    if (result.error) {
      // The dialog owns the failure message now; carry Stripe's specific reason
      // up so it can show "Your card was declined." rather than the generic copy.
      const message = result.error.message ?? undefined;
      setError(message ?? 'Payment could not be completed.');
      onResult?.(false, message);
      return;
    }
    onResult?.(true);
  }

  return (
    <div>
      <PaymentElement />
      <button
        type="button"
        style={confirmBtnStyle}
        data-testid="stripe-confirm"
        disabled={!stripe || submitting}
        aria-busy={submitting || undefined}
        onClick={confirm}
      >
        {submitting && (
          <Spinner
            size="sm"
            color="var(--color-text-inverse)"
            label="Processing"
            data-testid="stripe-confirm-spinner"
          />
        )}
        {/* Text cue stays beside the spinner so the wait is conveyed without motion. */}
        {submitting ? 'Processing…' : 'Confirm payment'}
      </button>
      {error && (
        <div role="alert" style={errorStyle} data-testid="stripe-error">
          {error}
        </div>
      )}
    </div>
  );
}

/**
 * The Stripe render adapter. Mounts the Payment Element against the session's
 * client secret. When Stripe.js is unavailable (no publishable key — a keyless
 * tier or the test env) it renders a stable, card-free placeholder so the
 * agnostic handoff still has a primary surface and tests can assert the mount.
 */
export function StripePaymentElement({
  descriptor,
  onResult,
}: {
  descriptor: SessionDescriptor;
  onResult?: ((ok: boolean, reason?: string) => void) | undefined;
}): JSX.Element {
  const stripe = useMemo(() => getStripe(), []);
  const clientSecret = descriptor.primary.sessionToken;

  if (!PUBLISHABLE_KEY && import.meta.env.MODE !== 'test') {
    // No publishable key baked into the bundle (the dev-APK blank-sheet bug:
    // CI never mapped VITE_STRIPE_PUBLISHABLE_KEY into the mobile build). Show a
    // user-readable message rather than an empty div so a missing key can never
    // silently blank the payment surface again. The test env keeps the silent
    // `render-adapter-stripe` placeholder below so the RTL stays green.
    return (
      <div data-testid="stripe-key-missing" style={keyMissingStyle} role="status">
        Payment is temporarily unavailable. Please try again later.
      </div>
    );
  }

  if (!stripe || !clientSecret) {
    // Gated / keyless: no live Stripe.js. Render the secure-mount placeholder
    // (no card fields) keyed for the descriptor so the handoff is still present.
    return (
      <div data-testid="render-adapter-stripe" data-session-token={clientSecret ?? ''}>
        {/* Stripe.js not configured on this tier — the Payment Element mounts in deployed envs. */}
      </div>
    );
  }

  return (
    <div data-testid="render-adapter-stripe" data-session-token={clientSecret}>
      <Elements stripe={stripe} options={{ clientSecret }}>
        <StripeConfirm onResult={onResult} />
      </Elements>
    </div>
  );
}
